// JSON-LD builders for Schema.org structured data.
// Used in route head() to embed structured data Google can index for
// rich results (vehicle listings, articles, local business panel).
import { CONTACT } from "@/config/contact";
import { getDealerGeo, validateDealerGeo } from "@/lib/dealer-geo";

const SITE = CONTACT.websiteUrl.replace(/\/$/, "");

// Per-builder dedupe: each builder warns at most once per process when its
// geo block falls back to omission. SSR renders this code on every request,
// so without the guard we'd flood logs. Reset is exported for tests.
const _warnedBuilders = new Set<string>();

function warnMissingGeoOnce(builder: string): void {
  if (_warnedBuilders.has(builder)) return;
  const v = validateDealerGeo();
  if (v.ok) return;
  _warnedBuilders.add(builder);
  console.warn(
    `[jsonld] ${builder}: omitting Schema.org geo block — null-safe fallback engaged. Issues: ${v.issues.join("; ")}`,
  );
}

/** Test-only: reset the per-builder warning guard so unit tests can assert
 *  the warning fires under different CONTACT fixtures. */
export function __resetJsonLdGeoWarningsForTests(): void {
  _warnedBuilders.clear();
}

const DAY_MAP: Record<string, string> = {
  Monday: "https://schema.org/Monday",
  Tuesday: "https://schema.org/Tuesday",
  Wednesday: "https://schema.org/Wednesday",
  Thursday: "https://schema.org/Thursday",
  Friday: "https://schema.org/Friday",
  Saturday: "https://schema.org/Saturday",
  Sunday: "https://schema.org/Sunday",
};

/**
 * AutoDealer + Organization + LocalBusiness — embed on `/` for Google's
 * Knowledge Graph + Business panel.
 *
 * Multi-typing the entity is intentional and Schema.org-valid:
 * - `AutoDealer` is the most specific type (sub-class of LocalBusiness).
 * - `LocalBusiness` is included explicitly so Google's LocalBusiness rich
 *   result eligibility is unambiguous (some validators only match the
 *   literal type string).
 * - `Organization` makes the entity eligible for Organization rich results
 *   (logo, sameAs, contact) and Knowledge Panel association.
 *
 * The single `@id` keeps every other JSON-LD object (Vehicle.offers.seller,
 * Review.itemReviewed, BlogPosting.publisher) referencing the same entity,
 * so Google merges all signals onto one business node.
 */
export function buildAutoDealerJsonLd(): string {
  const openingHours = CONTACT.openingHours
    .filter((o) => o.opens && o.closes)
    .map((o) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: o.days.map((d) => DAY_MAP[d]).filter(Boolean),
      opens: o.opens!,
      closes: o.closes!,
    }));

  const dealerGeo = getDealerGeo();
  if (!dealerGeo) warnMissingGeoOnce("buildAutoDealerJsonLd");

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": ["AutoDealer", "LocalBusiness", "Organization"],
    "@id": `${SITE}/#dealer`,
    name: CONTACT.companyName,
    legalName: CONTACT.companyName,
    url: SITE,
    telephone: CONTACT.phoneDisplay,
    email: CONTACT.email,
    description: CONTACT.description,
    image: `${SITE}/og-default.jpg`,
    logo: {
      "@type": "ImageObject",
      url: `${SITE}/og-default.jpg`,
      width: 1200,
      height: 630,
    },
    // VAT-id from CONTACT is also the CVR — Google accepts both.
    vatID: CONTACT.vatID,
    taxID: CONTACT.cvr,
    priceRange: "$$",
    currenciesAccepted: "DKK",
    paymentAccepted: "Bank Transfer, Financing",
    address: {
      "@type": "PostalAddress",
      streetAddress: CONTACT.address.street,
      postalCode: CONTACT.address.postalCode,
      addressLocality: CONTACT.address.city,
      addressCountry: CONTACT.address.country,
    },
    ...(dealerGeo
      ? {
          geo: {
            "@type": "GeoCoordinates",
            latitude: dealerGeo.latitude,
            longitude: dealerGeo.longitude,
          },
        }
      : {}),
    hasMap: CONTACT.mapsUrl,
    areaServed: {
      "@type": "Country",
      name: "Denmark",
    },
    openingHoursSpecification: openingHours,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: CONTACT.phoneDisplay,
      email: CONTACT.email,
      contactType: "sales",
      areaServed: "DK",
      availableLanguage: ["Danish", "English"],
    },
    sameAs: [CONTACT.social.facebook, CONTACT.social.instagram].filter(Boolean),
  });
}

/** WebSite schema with SearchAction — enables Google sitelinks searchbox. */
export function buildWebSiteJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    url: SITE,
    name: "AutoXChange",
    inLanguage: "da-DK",
    publisher: { "@id": `${SITE}/#dealer` },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE}/biler-til-salg?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  });
}

export interface VehicleJsonLdInput {
  id: string;
  slug: string;
  brand: string;
  model: string;
  variant?: string | null;
  year: number;
  mileage: number;
  price: number;
  fuelType?: string | null;
  transmission?: string | null;
  color?: string | null;
  bodyType?: string | null;
  doors?: number | null;
  seats?: number | null;
  horsepower?: number | null;
  engineSize?: string | null;
  description?: string | null;
  images?: string[] | null;
  status?: string | null;
  isLeasing?: boolean | null;
  leasingPrice?: number | null;
}

const FUEL_MAP: Record<string, string> = {
  benzin: "Gasoline",
  petrol: "Gasoline",
  diesel: "Diesel",
  el: "Electric",
  electric: "Electric",
  hybrid: "Hybrid",
  plugin_hybrid: "PluginHybrid",
};

const TRANS_MAP: Record<string, string> = {
  automatik: "Automatic",
  automatic: "Automatic",
  manual: "Manual",
  manuel: "Manual",
};

/** Vehicle schema for car detail pages — eligible for rich vehicle listings.
 *
 * Bygger en "Car" (mere specifik subtype af Vehicle) med Offer + Seller-ref
 * til vores AutoDealer for konsistente brand-snippets på Google.
 *
 * Sold / inactive cars
 * --------------------
 * When `status` is anything other than "active" we treat the listing as
 * permanently retired and emit Schema.org `Discontinued` instead of
 * `OutOfStock`. Rationale:
 *   - `OutOfStock` signals "temporarily unavailable, may return" — wrong
 *     for one-off used cars; Google may keep showing the price snippet
 *     until the offer is re-stocked.
 *   - `Discontinued` tells Google the SKU will not return, so the
 *     price/availability rich result is suppressed cleanly.
 * For sold cars we also backdate `priceValidUntil` to today (the offer is
 * no longer valid as of now) and clear `priceSpecification` semantics —
 * keeping the field present satisfies the Google Merchant requirement
 * while truthfully signaling that the price is historical.
 *
 * The page itself is still rendered with `noindex,nofollow,noarchive`
 * (set in the route head). The structured data exists so that any deep
 * links Google has already crawled get a definitive "discontinued" signal
 * the next time it revisits the URL — accelerating de-indexing.
 */
export function buildVehicleJsonLd(v: VehicleJsonLdInput): string {
  const url = `${SITE}/biler-til-salg/${v.slug || v.id}`;
  const name = `${v.brand} ${v.model}${v.variant ? " " + v.variant : ""} ${v.year}`;
  const isAvailable = (v.status ?? "active") === "active";

  // priceValidUntil:
  //  - active offer → 30 days forward (Google requires the field on Offer)
  //  - sold/discontinued → today (ISO), so the snippet is unambiguously stale
  const today = new Date();
  const validUntil = new Date(today);
  if (isAvailable) validUntil.setDate(validUntil.getDate() + 30);

  const transNorm = v.transmission?.toLowerCase();
  const fuelNorm = v.fuelType?.toLowerCase();

  const availability = isAvailable
    ? "https://schema.org/InStock"
    : "https://schema.org/Discontinued";

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Car",
    "@id": `${url}#vehicle`,
    name,
    url,
    brand: { "@type": "Brand", name: v.brand },
    manufacturer: { "@type": "Organization", name: v.brand },
    model: v.model,
    vehicleModelDate: String(v.year),
    productionDate: String(v.year),
    vehicleConfiguration: v.variant ?? undefined,
    mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage, unitCode: "KMT" },
    fuelType: fuelNorm ? FUEL_MAP[fuelNorm] || v.fuelType : undefined,
    vehicleTransmission: transNorm ? TRANS_MAP[transNorm] || v.transmission : undefined,
    color: v.color ?? undefined,
    bodyType: v.bodyType ?? undefined,
    numberOfDoors: v.doors ?? undefined,
    seatingCapacity: v.seats ?? undefined,
    vehicleEngine: v.horsepower
      ? {
          "@type": "EngineSpecification",
          enginePower: { "@type": "QuantitativeValue", value: v.horsepower, unitCode: "BHP" },
          ...(v.engineSize
            ? { engineDisplacement: { "@type": "QuantitativeValue", value: v.engineSize } }
            : {}),
        }
      : undefined,
    itemCondition: "https://schema.org/UsedCondition",
    description: v.description ?? undefined,
    image: v.images && v.images.length ? v.images.slice(0, 8) : undefined,
    offers: {
      "@type": "Offer",
      price: v.price,
      priceCurrency: "DKK",
      priceValidUntil: validUntil.toISOString().slice(0, 10),
      availability,
      itemCondition: "https://schema.org/UsedCondition",
      url,
      seller: { "@id": `${SITE}/#dealer` },
    },
  });
}

export interface ReviewJsonLdInput {
  id: string;
  author: string;
  authorLocation?: string | null;
  rating: number;
  quote: string;
  carPurchased?: string | null;
  /** ISO date string — when the review was created. */
  datePublished?: string | null;
}

/**
 * Review schema for individual customer testimonials.
 *
 * Each Review references the AutoDealer via `itemReviewed.@id` so Google can
 * attach concrete customer quotes to the business in search results (review
 * snippets / star excerpts). Use one Review object per published testimonial.
 */
export function buildReviewJsonLd(r: ReviewJsonLdInput): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Review",
    "@id": `${SITE}/om-os#review-${r.id}`,
    itemReviewed: { "@type": "AutoDealer", "@id": `${SITE}/#dealer` },
    author: {
      "@type": "Person",
      name: r.author,
      ...(r.authorLocation ? { address: { "@type": "PostalAddress", addressLocality: r.authorLocation } } : {}),
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    reviewBody: r.quote,
    ...(r.carPurchased ? { about: r.carPurchased } : {}),
    ...(r.datePublished ? { datePublished: r.datePublished } : {}),
  });
}

export interface ArticleJsonLdInput {
  slug: string;
  title: string;
  description?: string | null;
  image?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
}

/** BlogPosting schema for blog articles. */
export function buildArticleJsonLd(a: ArticleJsonLdInput): string {
  const url = `${SITE}/blog/${a.slug}`;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: a.title,
    description: a.description ?? undefined,
    image: a.image ?? `${SITE}/og-default.jpg`,
    datePublished: a.publishedAt ?? undefined,
    dateModified: a.updatedAt ?? a.publishedAt ?? undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@id": `${SITE}/#dealer`, "@type": "Organization", name: "AutoXChange" },
    publisher: { "@id": `${SITE}/#dealer` },
  });
}

export interface CarCollectionItem {
  id: string;
  slug: string | null;
  brand: string;
  model: string;
  variant?: string | null;
  year: number;
  price: number;
  image?: string | null;
}

/**
 * CollectionPage + ItemList for the `/biler-til-salg` listing page.
 *
 * Google uses CollectionPage to recognise category/listing pages (vs.
 * standalone product pages) and the embedded ItemList to surface "carousel"
 * style rich results when the page ranks. Each item is a lightweight
 * reference back to the canonical car detail page — Google fetches the
 * detail page's full Vehicle JSON-LD when it wants more.
 *
 * `numberOfItems` reflects the SSR snapshot of currently active inventory.
 * The listing route reuses this number for trust signals on-page so the
 * structured data and visible content always agree.
 */
export function buildCarCollectionJsonLd(input: {
  title: string;
  description: string;
  cars: CarCollectionItem[];
}): string {
  const url = `${SITE}/biler-til-salg`;
  const items = input.cars.slice(0, 50).map((c, i) => {
    const carUrl = `${SITE}/biler-til-salg/${c.slug || c.id}`;
    const name = `${c.brand} ${c.model}${c.variant ? " " + c.variant : ""} ${c.year}`;
    return {
      "@type": "ListItem",
      position: i + 1,
      url: carUrl,
      item: {
        "@type": "Car",
        "@id": `${carUrl}#vehicle`,
        name,
        url: carUrl,
        brand: { "@type": "Brand", name: c.brand },
        model: c.model,
        vehicleModelDate: String(c.year),
        ...(c.image ? { image: c.image } : {}),
        offers: {
          "@type": "Offer",
          price: c.price,
          priceCurrency: "DKK",
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/UsedCondition",
          url: carUrl,
          seller: { "@id": `${SITE}/#dealer` },
        },
      },
    };
  });

  const collectionGeo = getDealerGeo();
  if (!collectionGeo) warnMissingGeoOnce("buildCarCollectionJsonLd");

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name: input.title,
    description: input.description,
    inLanguage: "da-DK",
    isPartOf: { "@id": `${SITE}/#website` },
    about: { "@id": `${SITE}/#dealer` },
    ...(collectionGeo
      ? {
          spatialCoverage: {
            "@type": "Place",
            name: `${CONTACT.address.city}, ${CONTACT.address.country}`,
            geo: {
              "@type": "GeoCoordinates",
              latitude: collectionGeo.latitude,
              longitude: collectionGeo.longitude,
            },
          },
        }
      : {}),
    mainEntity: {
      "@type": "ItemList",
      name: "Aktuelle biler hos AutoXChange",
      numberOfItems: input.cars.length,
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      itemListElement: items,
    },
  });
}
